import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  getOhtClinics,
  type OhtClinic,
  type OhtClinicsResult,
} from "../../../../api/ohtAdmin";
import { Modal } from "../components/Modal";
import { SortCaret, useSortedRows } from "../components/useSortedRows";
import { PageOutline } from "../components/PageOutline";
import "../components/Skeleton.css";
import "../Overview/OverviewPage.css";
import "../components/Modal.css";
import "./ClinicsPage.css";

type SortKey =
  | "name"
  | "doctors"
  | "acceptingDoctors"
  | "city"
  | "fsa"
  | "applications30d"
  | "attachments30d"
  | "pipeline"
  | "avgTTA"
  | "declaredCapacity"
  | "usedCapacity"
  | "remainingCapacity"
  | "public";

type CapacityFilter = "all" | "hasCapacity" | "noCapacity" | "missing";
type PublicFilter = "all" | "eligible" | "incomplete";

type ClinicRow = {
  clinicId: number;
  name: string;
  city: string;
  fsa: string;
  address: string;
  phone: string;
  website: string;
  governanceStatus: string;
  isPublicEligible: boolean;
  doctorCount: number;
  acceptingDoctorCount: number;
  declaredCapacity: number;
  usedCapacity: number;
  remainingCapacity: number;
  applications30d: number;
  attachments30d: number;
  pipelineCount: number;
  avgTTA: number | null;
  latestCapacityUpdatedAt: string | null;
  latestAcceptingStatusUpdatedAt: string | null;
};

function fmtNumber(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  return Number(n).toLocaleString();
}

function fmtDays(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  return `${Number(n).toFixed(1)} d`;
}

function fmtDate(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-CA");
}

function compactAddress(clinic: OhtClinic): string {
  const parts = [clinic.addressLine1, clinic.addressLine2, clinic.cityName, clinic.postalCode]
    .map((p) => (p || "").trim())
    .filter(Boolean);
  return parts.join(", ");
}

function toClinicRow(clinic: OhtClinic): ClinicRow {
  return {
    clinicId: clinic.clinicId,
    name: clinic.clinicName,
    city: clinic.cityName || "",
    fsa: clinic.normalizedFsa || "",
    address: compactAddress(clinic),
    phone: clinic.phone || "",
    website: clinic.website || "",
    governanceStatus: clinic.governanceStatus || "—",
    isPublicEligible: !!clinic.isPublicEligible,
    doctorCount: clinic.doctorCount || 0,
    acceptingDoctorCount: clinic.acceptingDoctorCount || 0,
    declaredCapacity: clinic.declaredCapacity || 0,
    usedCapacity: clinic.usedCapacity || 0,
    remainingCapacity: clinic.remainingCapacity || 0,
    applications30d: clinic.applications30d || 0,
    attachments30d: clinic.attachments30d || 0,
    pipelineCount: clinic.pipelineCount || 0,
    avgTTA: clinic.avgDaysToAttachment ?? null,
    latestCapacityUpdatedAt: clinic.latestCapacityUpdatedAt,
    latestAcceptingStatusUpdatedAt: clinic.latestAcceptingStatusUpdatedAt,
  };
}

function StatusPill({
  label,
  tone = "neutral",
}: {
  label: string;
  tone?: "good" | "warn" | "danger" | "neutral";
}) {
  const palette: Record<NonNullable<React.ComponentProps<typeof StatusPill>["tone"]>, React.CSSProperties> = {
    good: { background: "#dcfce7", color: "#065f46" },
    warn: { background: "#fef3c7", color: "#92400e" },
    danger: { background: "#fee2e2", color: "#7f1d1d" },
    neutral: { background: "#f1f5f9", color: "#475569" },
  };

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        borderRadius: 999,
        padding: "3px 9px",
        fontSize: 12,
        fontWeight: 800,
        whiteSpace: "nowrap",
        ...palette[tone],
      }}
    >
      {label}
    </span>
  );
}

export default function ClinicsPage() {
  const [search, setSearch] = useState("");
  const [cityFilter, setCityFilter] = useState<string>("all");
  const [capacityFilter, setCapacityFilter] = useState<CapacityFilter>("all");
  const [publicFilter, setPublicFilter] = useState<PublicFilter>("all");

  const [result, setResult] = useState<OhtClinicsResult | null>(null);
  const [rows, setRows] = useState<ClinicRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedClinicId, setSelectedClinicId] = useState<number | null>(null);
  const [compareIds, setCompareIds] = useState<number[]>([]);
  const [compareOpen, setCompareOpen] = useState(false);

  const MAX_COMPARE = 3;

  function toggleCompare(id: number) {
    setCompareIds((prev) => {
      if (prev.includes(id)) return prev.filter((p) => p !== id);
      if (prev.length >= MAX_COMPARE) return prev;
      return [...prev, id];
    });
  }

  useEffect(() => {
    const controller = new AbortController();
    let cancel = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const live = await getOhtClinics(controller.signal);
        if (cancel) return;

        setResult(live);
        setRows((live.items || []).map(toClinicRow));
      } catch (err: any) {
        if (cancel || err?.name === "AbortError") return;
        setError(String(err?.message || "Failed to load OHT clinics."));
      } finally {
        if (!cancel) setLoading(false);
      }
    }

    load();

    return () => {
      cancel = true;
      controller.abort();
    };
  }, []);

  const cities = useMemo(() => {
    const set = new Set<string>();
    (rows || []).forEach((r) => r.city && set.add(r.city));
    return Array.from(set).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const q = search.trim().toLowerCase();

    return rows.filter((r) => {
      if (cityFilter !== "all" && r.city !== cityFilter) return false;
      if (capacityFilter === "hasCapacity" && r.remainingCapacity <= 0) return false;
      if (capacityFilter === "noCapacity" && r.remainingCapacity > 0) return false;
      if (capacityFilter === "missing" && r.declaredCapacity > 0) return false;
      if (publicFilter === "eligible" && !r.isPublicEligible) return false;
      if (publicFilter === "incomplete" && r.isPublicEligible) return false;

      if (!q) return true;

      return (
        r.name.toLowerCase().includes(q) ||
        r.city.toLowerCase().includes(q) ||
        r.fsa.toLowerCase().includes(q) ||
        r.address.toLowerCase().includes(q)
      );
    });
  }, [rows, search, cityFilter, capacityFilter, publicFilter]);

  const getSortValue = useCallback(
    (r: ClinicRow, key: SortKey): string | number | null => {
      switch (key) {
        case "name":
          return r.name;
        case "doctors":
          return r.doctorCount;
        case "acceptingDoctors":
          return r.acceptingDoctorCount;
        case "city":
          return r.city;
        case "fsa":
          return r.fsa;
        case "applications30d":
          return r.applications30d;
        case "attachments30d":
          return r.attachments30d;
        case "pipeline":
          return r.pipelineCount;
        case "avgTTA":
          return r.avgTTA;
        case "declaredCapacity":
          return r.declaredCapacity;
        case "usedCapacity":
          return r.usedCapacity;
        case "remainingCapacity":
          return r.remainingCapacity;
        case "public":
          return r.isPublicEligible ? 1 : 0;
      }
    },
    []
  );

  const { sorted, sort, toggleSort } = useSortedRows<ClinicRow, SortKey>(
    filtered,
    getSortValue,
    { key: "name", direction: "asc" }
  );

  const selected = rows?.find((r) => r.clinicId === selectedClinicId) || null;

  const filteredTotals = useMemo(() => {
    return filtered.reduce(
      (acc, row) => {
        acc.doctorCount += row.doctorCount;
        acc.acceptingDoctorCount += row.acceptingDoctorCount;
        acc.declaredCapacity += row.declaredCapacity;
        acc.usedCapacity += row.usedCapacity;
        acc.remainingCapacity += row.remainingCapacity;
        acc.applications30d += row.applications30d;
        acc.attachments30d += row.attachments30d;
        acc.pipelineCount += row.pipelineCount;
        return acc;
      },
      {
        doctorCount: 0,
        acceptingDoctorCount: 0,
        declaredCapacity: 0,
        usedCapacity: 0,
        remainingCapacity: 0,
        applications30d: 0,
        attachments30d: 0,
        pipelineCount: 0,
      }
    );
  }, [filtered]);

  return (
    <div className="oht-page">
      <header className="oht-page-header">
        <div className="oht-page-title-row">
          <div>
            <h1 className="oht-page-title">Clinics</h1>
            <p className="oht-page-subtitle">
              Clinic-level capacity, attachment flow, pipeline, and public-listing readiness across your OHT.
            </p>
          </div>
          <div className="oht-page-actions">
            {compareIds.length >= 2 ? (
              <button
                type="button"
                className="ocf-btn ocf-btn--primary"
                onClick={() => setCompareOpen(true)}
              >
                Compare ({compareIds.length})
              </button>
            ) : null}
          </div>
        </div>
      </header>

      <section className="ocf-card oht-section">
        <div className="oht-section-head">
          <h2 className="ocf-section-title">OHT clinic snapshot</h2>
          <span className="oht-section-hint">
            {result?.oht ? `${result.oht.name} · ${result.oht.code}` : "Live OHT scope"}
          </span>
        </div>

        <div className="oht-kpi-grid">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="ocf-card ocf-kpi">
                <span className="oht-skeleton" style={{ height: 13, width: "55%" }} />
                <span className="oht-skeleton" style={{ height: 28, width: "38%", marginTop: 12 }} />
                <span className="oht-skeleton" style={{ height: 12, width: "75%", marginTop: 12 }} />
              </div>
            ))
          ) : (
            <>
              <KpiTile
                label="Clinics"
                value={fmtNumber(result?.totals.clinicCount ?? filtered.length)}
                hint="Clinics matched to this OHT by FSA coverage."
              />
              <KpiTile
                label="Doctors"
                value={`${fmtNumber(result?.totals.acceptingDoctorCount)} / ${fmtNumber(result?.totals.doctorCount)}`}
                hint="Accepting doctors / total doctors across scoped clinics."
              />
              <KpiTile
                label="Remaining capacity"
                value={fmtNumber(result?.totals.remainingCapacity)}
                hint="Computed from doctor-level capacity and completed attachments."
              />
              <KpiTile
                label="Active pipeline"
                value={fmtNumber(result?.totals.pipelineCount)}
                hint="Requests currently submitted, in review, waitlisted, or accepted pending attachment."
              />
            </>
          )}
        </div>
      </section>

      <section className="ocf-card oht-filters">
        <h2 className="ocf-section-title">Filters</h2>
        <div className="oht-filters-grid" style={{ gridTemplateColumns: "repeat(4, minmax(0, 1fr))" }}>
          <div className="oht-filter">
            <label className="oht-filter-label" htmlFor="oht-clinic-search">
              Search
            </label>
            <input
              id="oht-clinic-search"
              className="ocf-select"
              type="search"
              value={search}
              placeholder="Clinic name, city, FSA, or address"
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="oht-filter">
            <label className="oht-filter-label" htmlFor="oht-clinic-city">
              City
            </label>
            <select
              id="oht-clinic-city"
              className="ocf-select"
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
            >
              <option value="all">All cities</option>
              {cities.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div className="oht-filter">
            <label className="oht-filter-label" htmlFor="oht-clinic-capacity">
              Capacity
            </label>
            <select
              id="oht-clinic-capacity"
              className="ocf-select"
              value={capacityFilter}
              onChange={(e) => setCapacityFilter(e.target.value as CapacityFilter)}
            >
              <option value="all">All capacity states</option>
              <option value="hasCapacity">Has remaining capacity</option>
              <option value="noCapacity">No remaining capacity</option>
              <option value="missing">Missing declared capacity</option>
            </select>
          </div>
          <div className="oht-filter">
            <label className="oht-filter-label" htmlFor="oht-clinic-public">
              Public listing
            </label>
            <select
              id="oht-clinic-public"
              className="ocf-select"
              value={publicFilter}
              onChange={(e) => setPublicFilter(e.target.value as PublicFilter)}
            >
              <option value="all">All listing states</option>
              <option value="eligible">Public eligible</option>
              <option value="incomplete">Incomplete / hidden</option>
            </select>
          </div>
        </div>
      </section>

      {error ? <div className="oht-page-error">{error}</div> : null}

      <section className="ocf-card oht-section">
        <div className="oht-section-head">
          <h2 className="ocf-section-title">Filtered totals</h2>
          <span className="oht-section-hint">
            {loading ? "Loading…" : `${filtered.length} clinic${filtered.length === 1 ? "" : "s"} selected by filters`}
          </span>
        </div>

        <div className="oht-kpi-grid">
          <KpiTile label="Doctors" value={fmtNumber(filteredTotals.doctorCount)} hint={`${fmtNumber(filteredTotals.acceptingDoctorCount)} accepting`} />
          <KpiTile label="Declared capacity" value={fmtNumber(filteredTotals.declaredCapacity)} hint={`${fmtNumber(filteredTotals.remainingCapacity)} remaining`} />
          <KpiTile label="30-day applications" value={fmtNumber(filteredTotals.applications30d)} hint={`${fmtNumber(filteredTotals.attachments30d)} completed attachments`} />
          <KpiTile label="Pipeline" value={fmtNumber(filteredTotals.pipelineCount)} hint="Active requests across filtered clinics" />
        </div>
      </section>

      <section className="ocf-card oht-table-card">
        <div className="oht-section-head">
          <h2 className="ocf-section-title">
            Clinics in OHT ({loading ? "…" : sorted.length})
          </h2>
          <span className="oht-section-hint">Click any column header to sort</span>
        </div>
        <div className="oht-table-wrap">
          <table className="oht-table">
            <colgroup>
              <col style={{ width: "40px" }} />
              <col style={{ width: "19%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "7%" }} />
              <col style={{ width: "11%" }} />
              <col style={{ width: "7%" }} />
              <col style={{ width: "9%" }} />
              <col style={{ width: "9%" }} />
              <col style={{ width: "9%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "7%" }} />
              <col style={{ width: "8%" }} />
            </colgroup>
            <thead>
              <tr>
                <th className="oht-table-compare-cell" aria-label="Compare" />
                <Th sortKey="name" label="Clinic" sort={sort} onSort={toggleSort} />
                <Th sortKey="doctors" label="Doctors" sort={sort} onSort={toggleSort} numeric />
                <Th sortKey="acceptingDoctors" label="Accepting" sort={sort} onSort={toggleSort} numeric />
                <Th sortKey="city" label="City" sort={sort} onSort={toggleSort} />
                <Th sortKey="fsa" label="FSA" sort={sort} onSort={toggleSort} />
                <Th sortKey="applications30d" label="Apps 30d" sort={sort} onSort={toggleSort} numeric />
                <Th sortKey="attachments30d" label="Attach 30d" sort={sort} onSort={toggleSort} numeric />
                <Th sortKey="pipeline" label="Pipeline" sort={sort} onSort={toggleSort} numeric />
                <Th sortKey="avgTTA" label="Avg TTA" sort={sort} onSort={toggleSort} numeric />
                <Th sortKey="declaredCapacity" label="Declared" sort={sort} onSort={toggleSort} numeric />
                <Th sortKey="remainingCapacity" label="Remaining" sort={sort} onSort={toggleSort} numeric />
                <Th sortKey="public" label="Public" sort={sort} onSort={toggleSort} />
                <th />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={`sk-${i}`} className="oht-table-skeleton-row">
                    {Array.from({ length: 14 }).map((__, j) => (
                      <td key={j}>
                        <span
                          className="oht-skeleton"
                          style={{
                            height: "13px",
                            width: j === 0 ? "18px" : j === 1 ? "80%" : "60%",
                          }}
                        />
                      </td>
                    ))}
                  </tr>
                ))
              ) : sorted.length === 0 ? (
                <tr>
                  <td colSpan={14} className="oht-table-empty">
                    No clinics match your filters.
                  </td>
                </tr>
              ) : (
                sorted.map((r) => {
                  const checked = compareIds.includes(r.clinicId);
                  const full = compareIds.length >= MAX_COMPARE;
                  return (
                    <tr key={r.clinicId} className={checked ? "oht-table-row--compare" : undefined}>
                      <td className="oht-table-compare-cell">
                        <input
                          type="checkbox"
                          aria-label={`Add ${r.name} to comparison`}
                          checked={checked}
                          disabled={!checked && full}
                          onChange={() => toggleCompare(r.clinicId)}
                        />
                      </td>
                      <td>
                        <button
                          className="oht-table-link"
                          type="button"
                          onClick={() => setSelectedClinicId(r.clinicId)}
                        >
                          {r.name}
                        </button>
                      </td>
                      <td className="oht-table-num">{fmtNumber(r.doctorCount)}</td>
                      <td className="oht-table-num">{fmtNumber(r.acceptingDoctorCount)}</td>
                      <td>{r.city || "—"}</td>
                      <td className="oht-table-mono">{r.fsa || "—"}</td>
                      <td className="oht-table-num">{fmtNumber(r.applications30d)}</td>
                      <td className="oht-table-num">{fmtNumber(r.attachments30d)}</td>
                      <td className="oht-table-num">{fmtNumber(r.pipelineCount)}</td>
                      <td className="oht-table-num">{fmtDays(r.avgTTA)}</td>
                      <td className="oht-table-num">{fmtNumber(r.declaredCapacity)}</td>
                      <td className="oht-table-num">{fmtNumber(r.remainingCapacity)}</td>
                      <td>
                        <StatusPill
                          label={r.isPublicEligible ? "Eligible" : "Incomplete"}
                          tone={r.isPublicEligible ? "good" : "warn"}
                        />
                      </td>
                      <td className="oht-table-num">
                        <button
                          className="ocf-btn ocf-btn--secondary oht-table-action"
                          type="button"
                          onClick={() => setSelectedClinicId(r.clinicId)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>

      <CompareModal
        open={compareOpen}
        clinicIds={compareIds}
        rows={rows || []}
        onClose={() => setCompareOpen(false)}
        onRemove={(id) => setCompareIds((prev) => prev.filter((p) => p !== id))}
      />

      <PageOutline
        whatItShows="This page shows the clinics that belong to your OHT area, with a practical summary of doctors, accepting capacity, recent patient activity, active pipeline, attachment progress, and whether each clinic listing is complete enough to appear publicly."
        audience="For OHT Admin users who need a clinic-level view of primary care attachment capacity and patient flow across their OHT."
        updateTrigger="The page refreshes when it opens. You can use the search box and filters to quickly narrow the clinic list by city, capacity status, or listing completeness."
        dataSources={[
          {
            label: "Clinic coverage",
            status: "live",
            detail: "Shows clinics that fall within this OHT’s assigned postal-code areas.",
          },
          {
            label: "Provider and capacity summary",
            status: "live",
            detail: "Shows how many family doctors are linked to each clinic, how many are accepting, and the clinic’s current reported capacity picture.",
          },
          {
            label: "Patient attachment activity",
            status: "live",
            detail: "Shows recent patient requests, completed attachments, active pipeline, and average time to attachment at the clinic level.",
          },
          {
            label: "Listing completeness",
            status: "live",
            detail: "Helps identify clinics that may need profile updates before patients can reliably find them online.",
          },
        ]}
        notes={[
          "Use this page to spot clinics with remaining capacity, clinics with active patient flow, and clinics that may need updated information.",
          "The comparison tool is for internal planning only and should not be interpreted as a public ranking of clinics.",
          "Incomplete clinics are highlighted so the OHT team can follow up and improve data quality.",
          "Filters only change what you see on screen; they do not change clinic records or patient requests.",
        ]}
      />

      <Modal
        open={selected != null}
        title={selected?.name || ""}
        eyebrow="Clinic detail"
        onClose={() => setSelectedClinicId(null)}
        size="md"
      >
        {selected ? (
          <dl className="oht-drawer-kv">
            <dt>City</dt>
            <dd>{selected.city || "—"}</dd>
            <dt>FSA</dt>
            <dd className="oht-table-mono">{selected.fsa || "—"}</dd>
            <dt>Address</dt>
            <dd>{selected.address || "—"}</dd>
            <dt>Phone</dt>
            <dd>{selected.phone || "—"}</dd>
            <dt>Governance status</dt>
            <dd>{selected.governanceStatus || "—"}</dd>
            <dt>Public listing</dt>
            <dd>
              <StatusPill
                label={selected.isPublicEligible ? "Eligible" : "Incomplete / hidden"}
                tone={selected.isPublicEligible ? "good" : "warn"}
              />
            </dd>
            <dt>Doctors</dt>
            <dd>
              {fmtNumber(selected.acceptingDoctorCount)} accepting / {fmtNumber(selected.doctorCount)} total
            </dd>
            <dt>Declared capacity</dt>
            <dd>{fmtNumber(selected.declaredCapacity)}</dd>
            <dt>Used capacity</dt>
            <dd>{fmtNumber(selected.usedCapacity)}</dd>
            <dt>Remaining capacity</dt>
            <dd>{fmtNumber(selected.remainingCapacity)}</dd>
            <dt>Applications in last 30 days</dt>
            <dd>{fmtNumber(selected.applications30d)}</dd>
            <dt>Attachments in last 30 days</dt>
            <dd>{fmtNumber(selected.attachments30d)}</dd>
            <dt>Active pipeline</dt>
            <dd>{fmtNumber(selected.pipelineCount)}</dd>
            <dt>Average time to attachment</dt>
            <dd>{selected.avgTTA != null ? `${selected.avgTTA.toFixed(1)} days` : "—"}</dd>
            <dt>Latest capacity update</dt>
            <dd>{fmtDate(selected.latestCapacityUpdatedAt)}</dd>
            <dt>Latest accepting status update</dt>
            <dd>{fmtDate(selected.latestAcceptingStatusUpdatedAt)}</dd>
          </dl>
        ) : null}
      </Modal>
    </div>
  );
}

function KpiTile({
  label,
  value,
  hint,
}: {
  label: string;
  value: React.ReactNode;
  hint: string;
}) {
  return (
    <div className="ocf-card ocf-kpi">
      <div className="ocf-kpi-label">{label}</div>
      <div className="ocf-big-number">{value}</div>
      <div className="ocf-muted">{hint}</div>
    </div>
  );
}

function CompareModal({
  open,
  clinicIds,
  rows,
  onClose,
  onRemove,
}: {
  open: boolean;
  clinicIds: number[];
  rows: ClinicRow[];
  onClose: () => void;
  onRemove: (id: number) => void;
}) {
  const selected = clinicIds
    .map((id) => rows.find((r) => r.clinicId === id))
    .filter(Boolean) as ClinicRow[];

  const winners = useMemo(() => {
    const out: {
      applications30d: number | null;
      attachments30d: number | null;
      attachmentRate: number | null;
      avgTTA: number | null;
      remainingCapacity: number | null;
      pipelineCount: number | null;
    } = {
      applications30d: null,
      attachments30d: null,
      attachmentRate: null,
      avgTTA: null,
      remainingCapacity: null,
      pipelineCount: null,
    };

    if (selected.length === 0) return out;

    const validNumbers = (values: (number | null)[]) => values.filter((v): v is number => v != null);
    const rates = selected.map((r) =>
      r.applications30d > 0 ? (r.attachments30d / r.applications30d) * 100 : null
    );

    const apps = validNumbers(selected.map((r) => r.applications30d));
    const atts = validNumbers(selected.map((r) => r.attachments30d));
    const tta = validNumbers(selected.map((r) => r.avgTTA));
    const remaining = validNumbers(selected.map((r) => r.remainingCapacity));
    const pipeline = validNumbers(selected.map((r) => r.pipelineCount));
    const validRates = validNumbers(rates);

    out.applications30d = apps.length ? Math.max(...apps) : null;
    out.attachments30d = atts.length ? Math.max(...atts) : null;
    out.avgTTA = tta.length ? Math.min(...tta) : null;
    out.remainingCapacity = remaining.length ? Math.max(...remaining) : null;
    out.pipelineCount = pipeline.length ? Math.min(...pipeline) : null;
    out.attachmentRate = validRates.length ? Math.max(...validRates) : null;

    return out;
  }, [selected]);

  return (
    <Modal
      open={open}
      title="Compare clinics"
      eyebrow={`${selected.length} clinics selected`}
      onClose={onClose}
      size="lg"
    >
      {selected.length === 0 ? (
        <div className="oht-muted">No clinics selected for comparison.</div>
      ) : (
        <div className="oht-compare-grid" style={{ gridTemplateColumns: `auto repeat(${selected.length}, minmax(0, 1fr))` }}>
          <div className="oht-compare-label">&nbsp;</div>
          {selected.map((r) => (
            <div key={r.clinicId} className="oht-compare-header">
              <div className="oht-compare-clinic-name">{r.name}</div>
              <div className="oht-compare-clinic-city">{r.city || "—"} · {r.fsa || "—"}</div>
              <button
                type="button"
                className="oht-compare-remove"
                aria-label={`Remove ${r.name} from comparison`}
                onClick={() => onRemove(r.clinicId)}
              >
                ×
              </button>
            </div>
          ))}

          <CompareRow
            label="Doctors"
            values={selected.map((r) => r.doctorCount)}
            winner={Math.max(...selected.map((r) => r.doctorCount))}
            winnerIsMax
            fmt={(v) => (v != null ? v.toLocaleString() : "—")}
          />
          <CompareRow
            label="Accepting doctors"
            values={selected.map((r) => r.acceptingDoctorCount)}
            winner={Math.max(...selected.map((r) => r.acceptingDoctorCount))}
            winnerIsMax
            fmt={(v) => (v != null ? v.toLocaleString() : "—")}
          />
          <CompareRow
            label="Applications 30d"
            values={selected.map((r) => r.applications30d)}
            winner={winners.applications30d}
            winnerIsMax
            fmt={(v) => (v != null ? v.toLocaleString() : "—")}
          />
          <CompareRow
            label="Attachments 30d"
            values={selected.map((r) => r.attachments30d)}
            winner={winners.attachments30d}
            winnerIsMax
            fmt={(v) => (v != null ? v.toLocaleString() : "—")}
          />
          <CompareRow
            label="Attachment rate"
            values={selected.map((r) =>
              r.applications30d > 0 ? (r.attachments30d / r.applications30d) * 100 : null
            )}
            winner={winners.attachmentRate}
            winnerIsMax
            fmt={(v) => (v != null ? `${v.toFixed(1)}%` : "—")}
          />
          <CompareRow
            label="Avg time to attachment"
            values={selected.map((r) => r.avgTTA)}
            winner={winners.avgTTA}
            winnerIsMax={false}
            fmt={(v) => (v != null ? `${v.toFixed(1)} days` : "—")}
          />
          <CompareRow
            label="Remaining capacity"
            values={selected.map((r) => r.remainingCapacity)}
            winner={winners.remainingCapacity}
            winnerIsMax
            fmt={(v) => (v != null ? v.toLocaleString() : "—")}
          />
          <CompareRow
            label="Pipeline"
            values={selected.map((r) => r.pipelineCount)}
            winner={winners.pipelineCount}
            winnerIsMax={false}
            fmt={(v) => (v != null ? v.toLocaleString() : "—")}
          />
        </div>
      )}
    </Modal>
  );
}

function CompareRow({
  label,
  values,
  winner,
  winnerIsMax,
  fmt,
}: {
  label: string;
  values: (number | null)[];
  winner: number | null;
  winnerIsMax: boolean;
  fmt: (v: number | null) => string;
}) {
  return (
    <>
      <div className="oht-compare-label">{label}</div>
      {values.map((v, i) => {
        const isWinner =
          v != null &&
          winner != null &&
          (winnerIsMax ? v >= winner : v <= winner);
        return (
          <div
            key={i}
            className={`oht-compare-value${isWinner ? " oht-compare-value--winner" : ""}`}
          >
            {fmt(v)}
            {isWinner ? <span className="oht-compare-badge">Best</span> : null}
          </div>
        );
      })}
    </>
  );
}

function Th({
  sortKey,
  label,
  sort,
  onSort,
  numeric,
}: {
  sortKey: SortKey;
  label: string;
  sort: { key: SortKey | null; direction: "asc" | "desc" };
  onSort: (k: SortKey) => void;
  numeric?: boolean;
}) {
  return (
    <th
      className={`oht-th-sortable ${numeric ? "oht-table-num" : ""}`}
      onClick={() => onSort(sortKey)}
      aria-sort={
        sort.key === sortKey
          ? sort.direction === "asc"
            ? "ascending"
            : "descending"
          : "none"
      }
    >
      <span className="oht-th-label">{label}</span>
      <SortCaret
        activeKey={sort.key}
        columnKey={sortKey}
        direction={sort.direction}
      />
    </th>
  );
}